import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, X, Copy, Check, Clock, Pencil, Banknote, Search } from 'lucide-react';
import Pagination from '../../components/admin/Pagination';
import { useAuth } from '../../App';
import {
  getManualPayments, createManualPayment, updateManualPayment,
  toggleManualPaymentPaid, deleteManualPayment
} from '../../api/manualPayments';
import AdminLayout from './AdminLayout';

const EMPTY = { reference: '', name: '', email: '', product: '', amount: '', note: '', status: 'oczekuje' };

const AdminManualPayments = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [summary, setSummary] = useState({ pending: 0, paid: 0, pending_sum: 0 });

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null); // freshly created record → show its reference
  const [copied, setCopied] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getManualPayments(token, { page, limit: 15, status: filter, search });
      setItems(data.items);
      setTotalPages(data.pages);
      setSummary(data.summary || { pending: 0, paid: 0, pending_sum: 0 });
    } catch (err) {} finally { setLoading(false); }
  }, [token, page, filter, search]);

  useEffect(() => { if (token) fetchItems(); }, [token, fetchItems]);

  // Debounce the search box (e-mail / imię / numer / produkt)
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const copy = (text, tag) => {
    navigator.clipboard?.writeText(text);
    setCopied(tag);
    setTimeout(() => setCopied(''), 1500);
  };

  const openCreate = () => { setForm(EMPTY); setEditId(null); setCreated(null); setModalOpen(true); };
  const openEdit = (it) => {
    setForm({ reference: it.reference || '', name: it.name || '', email: it.email || '', product: it.product || '', amount: it.amount ?? '', note: it.note || '', status: it.status || 'oczekuje' });
    setEditId(it.id); setCreated(null); setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editId) {
        await updateManualPayment(token, editId, payload);
        setModalOpen(false);
      } else {
        const rec = await createManualPayment(token, payload);
        setCreated(rec); // keep modal open, show the generated number
      }
      fetchItems();
    } catch (err) { alert('Błąd zapisu'); } finally { setSaving(false); }
  };

  const togglePaid = async (it) => {
    try { await toggleManualPaymentPaid(token, it.id); fetchItems(); } catch (err) {}
  };

  const remove = async (it) => {
    if (!window.confirm(`Usunąć ${it.reference}?`)) return;
    try { await deleteManualPayment(token, it.id); fetchItems(); } catch (err) {}
  };

  const filters = [
    { value: 'all', label: 'Wszystkie' },
    { value: 'oczekuje', label: 'Oczekujące' },
    { value: 'oplacono', label: 'Opłacone' },
  ];

  const fmt = (n) => (Number(n) || 0).toFixed(2);

  return (
    <AdminLayout title="Przelewy">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <div className="glass-card p-4">
          <p className="text-[#6B7280] text-xs mb-1">Oczekuje</p>
          <p className="text-2xl font-bold text-amber-400">{summary.pending}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-[#6B7280] text-xs mb-1">Do zapłaty (suma)</p>
          <p className="text-2xl font-bold text-white">{fmt(summary.pending_sum)} <span className="text-sm text-[#6B7280]">zł</span></p>
        </div>
        <div className="glass-card p-4">
          <p className="text-[#6B7280] text-xs mb-1">Opłacone</p>
          <p className="text-2xl font-bold text-[#10b981]">{summary.paid}</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          {filters.map(f => (
            <button key={f.value} onClick={() => { setFilter(f.value); setPage(1); }} className={`px-4 py-2 rounded-lg text-sm transition-all ${filter === f.value ? 'bg-[#10b981] text-white' : 'bg-[#0B0F0E] text-[#9CA3AF] border border-white/10 hover:border-[#10b981]'}`}>
              {f.label}
            </button>
          ))}
          <div className="relative w-full sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280] pointer-events-none" />
            <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder="Szukaj: e-mail, numer, imię…" className="w-full pl-9 pr-8" data-testid="payment-search" />
            {searchInput && (
              <button onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-white" title="Wyczyść">
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        <button onClick={openCreate} className="btn-primary" data-testid="add-payment-btn">
          <Plus size={18} /> Nowy przelew
        </button>
      </div>

      <div className="glass-card overflow-hidden">
        {loading ? <div className="flex justify-center py-12"><div className="spinner"></div></div> : (
          <>
            <div className="overflow-x-auto">
              <table className="table-custom">
                <thead>
                  <tr>
                    <th>Numer</th>
                    <th>Klient</th>
                    <th>Produkt</th>
                    <th>Kwota</th>
                    <th>Status</th>
                    <th>Data</th>
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? items.map(it => (
                    <tr key={it.id}>
                      <td>
                        <button onClick={() => copy(it.reference, it.id)} className="inline-flex items-center gap-1.5 font-mono text-white hover:text-[#10b981]" title="Kopiuj numer">
                          {it.reference}
                          {copied === it.id ? <Check size={14} className="text-[#10b981]" /> : <Copy size={14} className="text-[#6B7280]" />}
                        </button>
                      </td>
                      <td className="text-[#9CA3AF]">
                        <div>{it.name || '—'}</div>
                        {it.email && <div className="text-xs text-[#6B7280]">{it.email}</div>}
                      </td>
                      <td className="text-[#9CA3AF] max-w-[200px] truncate" title={it.product}>{it.product || '—'}</td>
                      <td className="text-white whitespace-nowrap">{fmt(it.amount)} zł</td>
                      <td>
                        <button onClick={() => togglePaid(it)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs transition-all ${it.status === 'oplacono' ? 'bg-[#10b981]/20 text-[#10b981] hover:bg-[#10b981]/30' : 'bg-amber-400/20 text-amber-400 hover:bg-amber-400/30'}`}
                          title="Kliknij, aby zmienić status">
                          {it.status === 'oplacono' ? <><Check size={13} /> Opłacone</> : <><Clock size={13} /> Oczekuje</>}
                        </button>
                      </td>
                      <td className="text-[#6B7280] whitespace-nowrap">{new Date(it.created_at).toLocaleDateString('pl-PL')}</td>
                      <td>
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(it)} className="p-2 text-[#9CA3AF] hover:text-[#10b981]" title="Edytuj"><Pencil size={16} /></button>
                          <button onClick={() => remove(it)} className="p-2 text-[#9CA3AF] hover:text-red-400" title="Usuń"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={7} className="text-center text-[#6B7280] py-8">Brak przelewów. Kliknij „Nowy przelew”, aby utworzyć numer dla klienta.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>

      {/* Create / Edit modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b border-white/5">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Banknote size={20} className="text-[#10b981]" />
                {editId ? 'Edytuj przelew' : (created ? 'Numer utworzony' : 'Nowy przelew')}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-[#6B7280] hover:text-white"><X size={24} /></button>
            </div>

            {created ? (
              <div className="p-6 space-y-5">
                <div className="text-center">
                  <p className="text-[#6B7280] text-sm mb-2">Podaj ten numer klientowi — wpisze go w <b>tytule przelewu</b>, a Ty dodasz go na fakturze.</p>
                  <div className="inline-flex items-center gap-3 bg-[#0B0F0E] border border-[#10b981]/40 rounded-xl px-5 py-4">
                    <span className="font-mono text-2xl font-bold text-white">{created.reference}</span>
                    <button onClick={() => copy(created.reference, 'big')} className="text-[#9CA3AF] hover:text-[#10b981]" title="Kopiuj numer">
                      {copied === 'big' ? <Check size={20} className="text-[#10b981]" /> : <Copy size={20} />}
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => copy(`Tytuł przelewu: ${created.reference}${created.amount ? ` · Kwota: ${fmt(created.amount)} zł` : ''}`, 'instr')}
                  className="btn-ghost w-full">
                  {copied === 'instr' ? <><Check size={16} /> Skopiowano</> : <><Copy size={16} /> Kopiuj dane do przelewu</>}
                </button>
                <div className="flex gap-3 pt-1">
                  <button onClick={() => { setCreated(null); setForm(EMPTY); }} className="btn-ghost flex-1">Dodaj kolejny</button>
                  <button onClick={() => setModalOpen(false)} className="btn-primary flex-1">Gotowe</button>
                </div>
              </div>
            ) : (
              <form onSubmit={save} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Numer / referencja</label>
                  <input type="text" value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} className="w-full font-mono" placeholder="np. PRZ-2609-01 — puste = wygeneruje się automatycznie" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#9CA3AF] mb-2">Klient (imię)</label>
                    <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full" placeholder="Jan Kowalski" />
                  </div>
                  <div>
                    <label className="block text-sm text-[#9CA3AF] mb-2">E-mail</label>
                    <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full" placeholder="klient@email.com" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Produkt / czego dotyczy</label>
                  <input type="text" value={form.product} onChange={e => setForm({ ...form, product: e.target.value })} className="w-full" placeholder="np. Kompletny pakiet 5 książek" />
                </div>
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Kwota (zł)</label>
                  <input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="w-full" placeholder="249" />
                </div>
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Status</label>
                  <div className="flex gap-2">
                    {[{ v: 'oczekuje', l: 'Oczekuje' }, { v: 'oplacono', l: 'Opłacone' }].map(s => (
                      <button key={s.v} type="button" onClick={() => setForm({ ...form, status: s.v })}
                        className={`flex-1 px-4 py-2 rounded-lg text-sm border transition-all ${form.status === s.v
                          ? (s.v === 'oplacono' ? 'bg-[#10b981]/20 text-[#10b981] border-[#10b981]/50' : 'bg-amber-400/20 text-amber-400 border-amber-400/50')
                          : 'bg-[#0B0F0E] text-[#9CA3AF] border-white/10 hover:border-[#10b981]'}`}>
                        {s.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Notatka (opcjonalnie)</label>
                  <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className="w-full" rows={2} placeholder="np. wysłano link do pobrania" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setModalOpen(false)} className="btn-ghost flex-1">Anuluj</button>
                  <button type="submit" disabled={saving} className="btn-primary flex-1">
                    {saving ? <div className="spinner"></div> : (editId ? 'Zapisz' : 'Utwórz numer')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminManualPayments;
